import { Inject, Injectable } from '@angular/core'
import { OpenLayersMapZoomService } from '../../../../../shared/open-layers/open-layers-map-zoom.service'
import VectorSource from 'ol/source/Vector'
import { OpenLayersMapService } from '../../../../../shared/open-layers/open-layers-map.service'
import { filter, take } from 'rxjs/operators'
import { GeneralMapService } from './general-map.service'
import { Store } from '@ngrx/store'
import { selectGeneralMapPadding, selectGeneralMapPaddingAndZoomedId } from '../state/general-map.selectors'

@Injectable({
    providedIn: 'root',
})
export class GeneralMapZoomService {

    constructor(
        @Inject(Store) protected store: Store,
        @Inject(GeneralMapService) protected generalMapService: GeneralMapService,
        @Inject(OpenLayersMapService) private openLayersMapService: OpenLayersMapService,
        @Inject(OpenLayersMapZoomService) private openLayersMapZoomService: OpenLayersMapZoomService,
    ) {
    }

    public zoomMapToMultipleLayers(mapId: string, layers): Promise<void> {
        return new Promise((resolve, reject) => {
            this.generalMapService.map$.pipe(
                filter(map => map[mapId] !== null && map[mapId] !== undefined), // Check if map is not null or undefined
                take(1), // Execute the code only once
            ).subscribe(map => {
                this.store.select(selectGeneralMapPadding(mapId)).pipe(
                    take(1)
                ).subscribe((padding) => {
                    setTimeout(() => this.openLayersMapZoomService.zoomMapToMultipleLayers(map[mapId], layers, padding))
                    resolve()
                })
            },error => reject(error))
        })
    }

    public zoomMapToAllFeaturesOfLayer(mapId: string, layerName: string): Promise<void> {
        return new Promise((resolve, reject) => {
            this.generalMapService.map$.pipe(
                filter(map => map[mapId] !== null && map[mapId] !== undefined), // Check if map is not null or undefined
                take(1), // Execute the code only once
            ).subscribe(map => {
                this.store.select(selectGeneralMapPadding(mapId)).pipe(
                    take(1)
                ).subscribe((padding) => {
                    const vectorSource: VectorSource<any> = this.openLayersMapService.getVectorSourceForLayerByName(layerName, map[mapId])

                    if (vectorSource?.getFeatures()?.length) {
                        setTimeout(() => {
                            if (this.generalMapService.getMap(mapId)) {
                                this.openLayersMapZoomService.zoomMapToAllFeaturesOfVectorSource(map[mapId], vectorSource, padding)
                            }
                        })
                    }
                    resolve()
                })
            },error => reject(error))
        })
    }

    public zoomMapToFeature(mapId: string, feature, custom_padding: number, duration = 0): Promise<void> {
        return new Promise((resolve, reject) => {
            this.generalMapService.map$.pipe(
                filter(map => map[mapId] !== null && map[mapId] !== undefined), // Check if map is not null or undefined
                take(1), // Execute the code only once
            ).subscribe(map => {
                this.store.select(selectGeneralMapPadding(mapId)).pipe(
                    take(1)
                ).subscribe((padding) => {
                const newPadding = custom_padding || padding
                    map[mapId].updateSize()
                    map[mapId].getView().fit(feature.getGeometry().getExtent(), {
                        padding: [newPadding, newPadding, newPadding, newPadding], //zoom out so user sees parcel surroundings
                        nearest: true, //disable fractional zoom
                        duration,
                        maxZoom: 12
                    })
                    resolve()
                })
            },error => reject(error))
        })
    }

    public updateMapViewByZoomedId(mapId: string): Promise<void> {
        return new Promise((resolve, reject) => {
            this.generalMapService.map$.pipe(
                filter(map => map[mapId] !== null && map[mapId] !== undefined), // Check if map is not null or undefined
                take(1), // Execute the code only once
            ).subscribe(map => {
                this.store.select(selectGeneralMapPaddingAndZoomedId(mapId)).pipe(
                    take(1)
                ).subscribe(({padding, zoomedId}) => {
                    if (map[mapId] && zoomedId?.layerName) {
                        const vectorSource: VectorSource<any> = this.openLayersMapService.getVectorSourceForLayerByName(zoomedId.layerName, map[mapId])

                        if (vectorSource) {
                            setTimeout(() => {
                                if (this.generalMapService.getMap(mapId)) {
                                    this.openLayersMapZoomService.zoomMapToFeatureIdOfVectorSource(zoomedId.featureId, vectorSource, map[mapId], padding)
                                }
                            })
                        }
                    }
                    resolve()
                })
            },error => reject(error))
        })
    }
}